import { useEffect, useMemo, useState } from "react";
import { useProfile } from "../context/ProfileContext";
import { httpService } from "../services/httpService";

// Local YYYY-MM-DD (avoids the UTC shift that toISOString() causes in IST)
const toLocalYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// "7_days" -> 7, "2_weeks" -> 14, "1_month" -> 30
const parseWindowToDays = (time) => {
  const match = String(time || "").match(/(\d+)\s*_?\s*(day|week|month)?/i);
  if (!match) return 0;
  const n = parseInt(match[1], 10);
  const unit = (match[2] || "day").toLowerCase();
  if (unit === "week") return n * 7;
  if (unit === "month") return n * 30;
  return n;
};

// ---- live fetch (shared by all drawers, so they don't each hit the API) ----
let inFlight = null;
let lastValue; // undefined = never fetched, null = fetched but no setting
let lastFetchedAt = 0;
const CACHE_MS = 5000;

const fetchLiveSetting = async () => {
  if (inFlight) return inFlight;
  if (lastValue !== undefined && Date.now() - lastFetchedAt < CACHE_MS) {
    return lastValue;
  }
  inFlight = httpService
    .get("/vendors/getOnlineProfile", { skipToast: true })
    .then((res) => {
      const body = res?.data;
      // handles both response shapes: body = profile, or body = { data: profile }
      const profile = body?.systemSettings ? body : body?.data;
      lastValue = profile?.systemSettings?.allow_past_booking ?? null;
      lastFetchedAt = Date.now();
      return lastValue;
    })
    .catch(() => undefined) // on failure, fall back to the context value
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};

export const invalidateAllowPastBookingCache = () => {
  lastValue = undefined;
  lastFetchedAt = 0;
};

export const startOfLocalDay = (value) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const isBeforeMinBookingDate = (date, minDate) => {
  if (!date || !minDate) return false;
  return startOfLocalDay(date) < startOfLocalDay(minDate);
};

export const pastBookingBlockedMessage = (
  allowPastBooking,
  maxPastDays,
  noun = "Bookings",
) =>
  allowPastBooking
    ? `${noun} can only be created up to ${maxPastDays} day${maxPastDays === 1 ? "" : "s"} back.`
    : `${noun} cannot be created for past dates.`;

export const useAllowPastBooking = () => {
  const { profileData } = useProfile();
  const [liveSetting, setLiveSetting] = useState(undefined);

  // Fetch the latest setting every time a drawer using this hook mounts (i.e. opens)
  useEffect(() => {
    let cancelled = false;
    fetchLiveSetting().then((value) => {
      if (!cancelled && value !== undefined) setLiveSetting(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Live value wins; the context value is only the fallback until the fetch returns
  const setting =
    liveSetting !== undefined
      ? liveSetting
      : profileData?.systemSettings?.allow_past_booking;

  return useMemo(() => {
    const allowPastBooking = setting?.enabled === true;
    const parsedDays = parseWindowToDays(setting?.time);
    const maxPastDays = allowPastBooking ? (parsedDays > 0 ? parsedDays : 7) : 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() - maxPastDays);

    return {
      allowPastBooking,
      maxPastDays,
      minDate,
      minDateStr: toLocalYMD(minDate),
      todayStr: toLocalYMD(today),
    };
  }, [setting?.enabled, setting?.time]);
};